import React, { createRef, useState } from 'react'
import styled from 'styled-components'

import { Helmet } from 'react-helmet'
import { NavLink as RouterNavLink, NavLinkProps } from 'react-router-dom'
import {
  Breadcrumbs as MuiBreadcrumbs,
  Button as MuiButton,
  Divider as MuiDivider,
  Grid as MuiGrid,
  Link,
  Typography as MuiTypography,
} from '@material-ui/core'
import { spacing } from '@material-ui/system'
import { Add as AddIcon, CloudDownload as MuiCloudDownload } from '@material-ui/icons'

import {
  useQueryPaginator,
  CustomField,
  DateTimeField,
  LinkedField,
  OrderStatusField,
  PaginatedTable,
  ValueField,
} from 'src/components/Table'
import { ExportDialog } from 'src/components/Dialog'
import { BulkOrderUploadDialog } from 'src/modules/client/components/parcel/BulkOrderUploadDialog'
import { ParcelOrderDialog } from 'src/modules/client/components/parcel/OrderDialog'

import { skuit } from 'src/services/skuit'
import toast from 'react-hot-toast'
import { Notification } from 'src/components/Notification'

const Button = styled(MuiButton)(spacing)
const Grid = styled(MuiGrid)(spacing)
const Typography = styled(MuiTypography)(spacing)

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

const CloudDownload = styled(MuiCloudDownload)(spacing)
const Divider = styled(MuiDivider)(spacing)

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing)

export const OrderListPage = () => {
  const [bulkOrderDialogOpen, setBulkOrderDialogOpen] = useState(false)
  const [orderDialogOpen, setOrderDialogOpen] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)
  const [bulkErrors, setBulkErrors] = useState<any[]>([])

  const paginator = useQueryPaginator('order-list', skuit.client.parcel.orders.list)
  const customFieldPaginator = useQueryPaginator('custom-fields', skuit.client.parcel.orders.customFields.list)

  const csvOrdersFileInput = createRef<HTMLInputElement>()
  const onCsvOrdersFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setBulkErrors([])
      const data = {
        file: e.target.files[0],
      }
      skuit.client.parcel.orders
        .uploadCsv({ data })
        .then(() => {
          paginator.refresh()
        })
        .catch((error) => {
          setBulkErrors(error.response.data)
          toast.error(() => <Notification message={`Failed to process CSV: ${error}`} />)
        })
    }
  }

  return (
    <>
      <Helmet title="Parcel Orders" />
      {orderDialogOpen && (
        <ParcelOrderDialog
          open={orderDialogOpen}
          handleClose={() => {
            setOrderDialogOpen(false)
          }}
          onSave={paginator.refresh}
        />
      )}
      {bulkOrderDialogOpen && (
        <BulkOrderUploadDialog
          open={bulkOrderDialogOpen}
          handleClose={() => {
            setBulkOrderDialogOpen(false)
          }}
          onSuccess={paginator.refresh}
        />
      )}

      <Grid justify="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Parcel Orders
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} exact to="/">
              Dashboard
            </Link>
            <Typography>Parcel Orders</Typography>
          </Breadcrumbs>
        </Grid>

        <Grid item>
          <Grid container spacing={6}>
            <Grid item>
              <div>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setOrderDialogOpen(true)}
                >
                  <AddIcon />
                  New Order
                </Button>
              </div>
            </Grid>
            <Grid item>
              <div>
                <input
                  type="file"
                  accept=".csv"
                  ref={csvOrdersFileInput}
                  style={{ display: 'none' }}
                  onChange={onCsvOrdersFileSelected}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setBulkOrderDialogOpen(true)}
                >
                  <AddIcon />
                  Bulk Create Parcel Orders
                </Button>
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Divider my={6} />

      <Grid container spacing={6}>
        {!!bulkErrors && !!bulkErrors.length && (
          <Grid item xs={12}>
            <Typography color="error">Errors in CSV:</Typography>
            {bulkErrors.map((error: any, index: number) => (
              <Typography key={index} color="error">
                Line {error.line}: Order: {error.order}: {JSON.stringify(error.details)}
              </Typography>
            ))}
          </Grid>
        )}
        <Grid item xs={12}>
          <Button variant="contained" onClick={() => setExportOpen(true)}>
            <CloudDownload mr={2} /> Export Parcel Orders to CSV
          </Button>
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Parcel Orders"
            fields={[
              { id: 'number', label: 'Number', renderer: LinkedField(ValueField) },
              { id: 'placedOn', label: 'Date Placed', renderer: DateTimeField },
              { id: 'fulfillOn', label: 'Date to Fulfill By', renderer: DateTimeField },
              { id: 'status', label: 'Status', renderer: OrderStatusField },
              { id: 'customFields', label: 'Custom Fields', renderer: CustomField },
            ]}
            paginator={paginator}
          />
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" onClick={() => setExportOpen(true)}>
            <CloudDownload mr={2} /> Export Parcel Orders to CSV
          </Button>
        </Grid>
      </Grid>
      <ExportDialog
        name="Parcel Orders"
        download={skuit.client.parcel.orders.export}
        open={exportOpen}
        fields={[
          'number',
          'placed_on',
          'fulfill_by',
          'details',
          'consignee',
          'address',
          'status',
          'custom1',
          'custom2',
          'custom3',
        ].concat(customFieldPaginator.data.results.map((cf) => cf.name))}
        handleClose={() => setExportOpen(false)}
      />
    </>
  )
}
